<template>
	<div class="flex_1 margin_horizontal">
		<AccessRestrictedButtonGroup v-show="false"
									 class="flex flex_center table_button_group"
									 v-model="activeButton"
									 :elements="buttonGroupTypes"
									 :session-key="$route.path"
		/>
		<el-tabs class="table_tabs margin_top" v-model="activeAccountTab">
			<el-tab-pane v-for="account in accountList"
						 :name="String(account.id)" :key="account.id"
						 :disabled="!account.operational"
			>
				<div slot="label" class="accountDisplay">
					<el-tooltip popper-class="tooltip-md" placement="bottom"
								:content="$t('exchange.missingBuyPermissions')"
								:open-delay="500"
								:disabled="account.operational"
					>
						<div>
							{{ account.tag || account.tbIban }}
							<br/>
							{{ formatAccountBalance(account) }}
						</div>
					</el-tooltip>
				</div>
			</el-tab-pane>
		</el-tabs>
		<ExchangeReceivablesTable
			v-if="activeButton && activeButton.name === buttonGroupTypes.receivablesExchange.name"
			:active-account="activeAccount"
			:rating-config="ratingConfig"
			:tci-confirmation-status="tciConfirmationStatus"
			:ready="allRequirementsLoaded"
			:accounts-warning-text="accountsWarningText"
		/>
	</div>
</template>

<script>
import ExchangeReceivablesTable
	from "@/components/templates/investor/exchange/receivables/ExchangeReceivablesTable.vue";
import ExchangeRevolvingPutOptionsTable
	from "@/components/templates/investor/exchange/revolving/ExchangeRevolvingPutOptionsTable.vue";
import {exchangeReceivablesApi} from "@/common/api/exchangeReceivablesApi";
import {customRatingsApi} from "@/common/api/customRatingsApi";
import {formatAsMoneyAmount} from "@/common/format";
import {enumDef} from "@/common/enumDefs";
import AccessRestrictedButtonGroup from "@/components/templates/common/permissions/AccessRestrictedButtonGroup";
import {navigationTabs} from "@/common/navigation/navigationTabs";
import {businessConfigApi} from "@/common/api/businessConfigApi";
import {mapActions} from "vuex";

export default {
	components: {
		AccessRestrictedButtonGroup,
		ExchangeReceivablesTable,
		ExchangeRevolvingPutOptionsTable
	},
	data() {
		let buttonGroupTypes = {
			receivablesExchange: {
				name: navigationTabs.investor.exchange.receivables,
				translationKey: "exchange.receivablesExchange",
				api: exchangeReceivablesApi,
				permission: enumDef.permissions.InvestorExchangeReceivables
			},
		};

		return {
			activeButton: null,
			buttonGroupTypes: buttonGroupTypes,
			ratingConfig: null,
			ratingConfigLoaded: false,
			tciConfirmationStatus: null,
			tciConfirmationStatusLoaded: false,
			activeAccountTab: "-1",
			accountList: null,
			accountsWarningText: null,
		};
	},
	computed: {
		activeAccountId() {
			return Number(this.activeAccountTab);
		},
		activeAccount() {
			return this.accountList ? this.accountList.find(account => account.id === this.activeAccountId) : null;
		},
		allRequirementsLoaded() {
			return this.activeAccount && this.ratingConfigLoaded && this.tciConfirmationStatusLoaded;
		}
	},
	created() {
		businessConfigApi.getAuctions()
			.then(this.setActionAuctionConfig)
			.catch(this.toast);
	},
	methods: {
		...mapActions(["setActionAuctionConfig"]),
		loadData() {
			let setupLoadFunctions = [
				!this.ratingConfigLoaded ? this.loadRatingConfig() : Promise.resolve(),
				!this.tciConfirmationStatusLoaded ? this.loadTciConfirmationStatus() : Promise.resolve(),
				this.loadAccounts()
			];
			Promise.all(setupLoadFunctions)
				.then(() => {})
				.catch(() => {});
		},
		loadTciConfirmationStatus() {
			return new Promise((resolve => {
				exchangeReceivablesApi.inspection.getCreditInsurancePolicyHolderStatus()
					.then(res => {
						this.tciConfirmationStatus = res;
						resolve();
					})
					.catch(() => {
						// We ignore errors for the tci status and show the exchange as if no status exists
						this.tciConfirmationStatus = enumDef.creditInsurancePolicyHolderStatus.NONE;
						resolve();
					}).finally(() => {
						this.tciConfirmationStatusLoaded = true;
					});
			}))
		},
		loadRatingConfig() {
			return new Promise((resolve => {
				customRatingsApi.inspection.getConfig()
					.then(res => {
						this.ratingConfig = res;
						resolve();
					})
					.catch(() => {
						// We ignore errors for the config and show the exchange as if no config exists
						resolve();
					}).finally(() => {
						this.ratingConfigLoaded = true;
					});
			}));
		},
		loadAccounts() {
			return new Promise((resolve, reject) => {
				this.activeButton.api.inspection.getBuyAccounts()
					.then((res) => {
						this.accountList = res;

						if (this.accountList.length <= 0) {
							this.accountsWarningText = this.$t("common.noAccountsAssigned");
							reject();
							return;
						}

						let firstOperationalAccount = this.accountList.findIndex(account => account.operational);
						if (firstOperationalAccount === -1) {
							this.accountsWarningText = this.$t("exchange.noAccountsWithBuyPermissions");
							reject();
							return;
						}

						let savedAccountTab = this.$store.getters["session/activeTabs"][this.$route.path.concat("&account")];
						if (this.accountList.find(account => account.id === Number(savedAccountTab))) {
							this.activeAccountTab = savedAccountTab;
						}

						if (this.activeAccountTab === "-1") {
							this.activeAccountTab = String(this.accountList[firstOperationalAccount].id);
						}
						resolve();
					})
					.catch((e) => {
						this.toast(e);
						reject();
					});
			});
		},
		formatAccountBalance(account) {
			if (!account.operational) {
				return account.tbCurrType;
			}

			return formatAsMoneyAmount(account.availableAmount, {
				currency: account.tbCurrType
			});
		},
	},
	watch: {
		activeButton() {
			this.loadData();
		},
		activeAccountTab() {
			this.$store.dispatch("session/setActiveTab", { tabGroup: this.$route.path.concat("&account"), activeTab: this.activeAccountTab });
		}
	}
};
</script>

<style lang="scss" scoped>
.accountDisplay {
	text-align: center;
	line-height: 20px;
}
</style>
